import { Text } from '@magicschool/spellbook/components/Text';
import type { CartesianTickItem } from 'recharts/types/util/types';

/** Props passed by Recharts to custom axis tick components */
export interface AxisTickProps {
  /** X coordinate of the tick */
  x: number;
  /** Y coordinate of the tick */
  y: number;
  /** Total width of the axis */
  width: number;
  /** Number of visible ticks on the axis */
  visibleTicksCount: number;
  /** The tick data containing the label value */
  payload: CartesianTickItem;
  /** Index of this tick in the data array */
  index: number;
  /** The chart data array (passed from BarChart) */
  data: Array<{ name?: string; subLabel?: string; [key: string]: unknown }>;
}

const LABEL_PADDING = 8;

/**
 * Custom X-axis tick component that truncates labels to fit within bar width.
 * Uses CSS text-overflow ellipsis to handle long labels gracefully.
 * Supports optional two-line labels with subLabel.
 */
export const TruncatedXAxisTick = ({ x, y, payload, width, visibleTicksCount, index, data }: AxisTickProps) => {
  if (visibleTicksCount === 0) return null;

  const dataItem = data[index];
  const name = dataItem?.name ? String(dataItem.name) : String(payload.value);
  const subLabel = dataItem?.subLabel ? String(dataItem.subLabel) : undefined;

  const tickWidth = width / visibleTicksCount;
  const labelWidth = tickWidth - LABEL_PADDING;
  const labelHeight = subLabel ? 40 : 20;

  return (
    <foreignObject x={x - labelWidth / 2} y={y} width={labelWidth} height={labelHeight}>
      <div className="flex flex-col items-center justify-start h-full w-full">
        <Text variant="caption-1" className="truncate block text-center text-bar-chart-axis-tick-label w-full" title={name}>
          {name}
        </Text>
        {subLabel && (
          <Text variant="caption-2" className="truncate block text-center text-bar-chart-axis-tick-label w-full" title={subLabel}>
            {subLabel}
          </Text>
        )}
      </div>
    </foreignObject>
  );
};
